import {
    createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { DateTime } from 'luxon';
import { useQueryClient } from 'react-query';
import { Column } from 'react-rainbow-components';
import {
    hideAppSpinner,
    showAppNotification,
    showAppSpinner,
} from '@rainbow-modules/app';
import Table from 'components/Table';
import ButtonIcon from 'components/ButtonIcon';
import ChevronLeft from 'components/icons/chevronLeft';
import ChevronRight from 'components/icons/chevronRight';
import useSearchFilters from 'hooks/useSearchFilters';
import {
    InvoicePatchBody, InvoiceRow, InvoiceStatus, ListInvoicesQuery,
} from '../types';
import { INVOICE_FILTER_NAMES, INVOICE_PAGE_SIZE } from '../constants';
import {
    isAppUnavailable, useInvoiceList, useInvoiceSigningStates, usePatchInvoice,
} from '../api/hooks';
import VoidInvoiceModal from '../VoidInvoiceModal';
import { canEditInvoice, toIsoDate } from '../helpers';
import {
    TableContainer,
    PaginationContainer,
    EntriesText,
    PaginationButtons,
    EmptyState,
    EmptyTitle,
    EmptyDescription,
} from '../styled';
import StatusColumn from './columns/status';
import ActionsColumn from './columns/actions';
import InvoiceColumn, {
    AmountColumn,
    CustomerColumn,
    DueColumn,
    IssuedColumn,
} from './columns/cells';

interface InvoiceListProps {
    organizationId: string;
    customerId?: string;
    hideCustomerColumn?: boolean;
    filterNames?: string[];
    onView: (invoice: InvoiceRow) => void;
    onEdit: (invoice: InvoiceRow) => void;
    onPrint: (invoice: InvoiceRow) => void;
    onDownload: (invoice: InvoiceRow) => void;
}

interface InvoiceActionsContextValue {
    organizationId: string;
    onView: (invoice: InvoiceRow) => void;
    onEdit: (invoice: InvoiceRow) => void;
    onPrint: (invoice: InvoiceRow) => void;
    onDownload: (invoice: InvoiceRow) => void;
    onMarkSent: (invoice: InvoiceRow) => void;
    onMarkPaid: (invoice: InvoiceRow) => void;
    onVoid: (invoice: InvoiceRow) => void;
}

const InvoiceActionsContext = createContext<InvoiceActionsContextValue>({
    organizationId: '',
    onView: () => undefined,
    onEdit: () => undefined,
    onPrint: () => undefined,
    onDownload: () => undefined,
    onMarkSent: () => undefined,
    onMarkPaid: () => undefined,
    onVoid: () => undefined,
});

const TableActions = ({ row }: { row?: InvoiceRow & { canEdit?: boolean } }) => {
    const {
        organizationId,
        onView,
        onEdit,
        onPrint,
        onDownload,
        onMarkSent,
        onMarkPaid,
        onVoid,
    } = useContext(InvoiceActionsContext);
    return (
        <ActionsColumn
            row={row}
            organizationId={organizationId}
            onView={onView}
            onEdit={onEdit}
            onPrint={onPrint}
            onDownload={onDownload}
            onMarkSent={onMarkSent}
            onMarkPaid={onMarkPaid}
            onVoid={onVoid}
        />
    );
};

const InvoiceList = ({
    organizationId,
    customerId: lockedCustomerId,
    hideCustomerColumn,
    filterNames = INVOICE_FILTER_NAMES,
    onView,
    onEdit,
    onPrint,
    onDownload,
}: InvoiceListProps) => {
    const queryClient = useQueryClient();
    const { mutateAsync: patchInvoice, isLoading: isPatching } = usePatchInvoice(organizationId);
    const [voidTarget, setVoidTarget] = useState<InvoiceRow | undefined>();
    const { values } = useSearchFilters({ filterNames });
    const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
    const [pageIndex, setPageIndex] = useState(0);

    const filterCustomerId = typeof values.customerId === 'string' ? values.customerId : undefined;
    const customerId = lockedCustomerId || filterCustomerId;
    const status = typeof values.status === 'string'
        ? values.status as InvoiceStatus
        : undefined;
    const issuedRange = useMemo(() => {
        if (typeof values.issuedOn !== 'string' || !values.issuedOn) return [];
        return values.issuedOn.split(',').map((strDate) => DateTime.fromISO(strDate).toJSDate());
    }, [values.issuedOn]);
    const dueRange = useMemo(() => {
        if (typeof values.dueOn !== 'string' || !values.dueOn) return [];
        return values.dueOn.split(',').map((strDate) => DateTime.fromISO(strDate).toJSDate());
    }, [values.dueOn]);

    const issuedFrom = issuedRange[0] ? toIsoDate(issuedRange[0]) : undefined;
    const issuedTo = issuedRange[1]
        ? toIsoDate(issuedRange[1])
        : issuedFrom;
    const dueFrom = dueRange[0] ? toIsoDate(dueRange[0]) : undefined;
    const dueTo = dueRange[1]
        ? toIsoDate(dueRange[1])
        : dueFrom;

    useEffect(() => {
        setCursors([undefined]);
        setPageIndex(0);
    }, [customerId, status, issuedFrom, issuedTo, dueFrom, dueTo]);

    const query: ListInvoicesQuery = useMemo(() => ({
        customerId,
        status,
        issuedFrom,
        issuedTo: issuedRange.length > 1 ? issuedTo : issuedFrom,
        dueFrom,
        dueTo: dueRange.length > 1 ? dueTo : dueFrom,
        cursor: cursors[pageIndex],
        limit: INVOICE_PAGE_SIZE,
    }), [
        customerId,
        status,
        issuedFrom,
        issuedTo,
        issuedRange.length,
        dueFrom,
        dueTo,
        dueRange.length,
        cursors,
        pageIndex,
    ]);

    const {
        data, isLoading, error, isFetching,
    } = useInvoiceList(organizationId, query);

    const invoices = useMemo(() => data?.data.invoices || [], [data]);
    const signing = useInvoiceSigningStates(organizationId, invoices);
    const rows = useMemo(
        () => invoices.map((invoice) => {
            const check = signing[invoice.id];
            return {
                ...invoice,
                signatureState: check?.info?.state,
                signature: check?.info,
                canEdit: canEditInvoice(invoice.status, check?.editState),
            };
        }),
        [invoices, signing],
    );
    const nextCursor = data?.data.nextCursor || null;
    const unavailable = isAppUnavailable(error);

    const goNext = useCallback(() => {
        if (!nextCursor) return;
        setCursors((prev) => {
            const next = [...prev];
            next[pageIndex + 1] = nextCursor;
            return next;
        });
        setPageIndex((prev) => prev + 1);
    }, [nextCursor, pageIndex]);

    const goPrev = useCallback(() => {
        setPageIndex((prev) => Math.max(0, prev - 1));
    }, []);

    const runCommand = useCallback(async (invoice: InvoiceRow, body: InvoicePatchBody) => {
        showAppSpinner();
        try {
            if (body.type === 'markPaid' && invoice.status === 'draft') {
                await patchInvoice({ invoiceId: invoice.id, body: { type: 'markSent' } });
            }
            await patchInvoice({ invoiceId: invoice.id, body });
            const descriptions: Record<InvoicePatchBody['type'], string> = {
                markSent: 'Invoice marked as sent.',
                markPaid: 'Invoice marked as paid.',
                void: 'Invoice marked as void.',
            };
            showAppNotification({
                title: 'Invoice updated',
                description: descriptions[body.type],
                icon: 'success',
                timeout: 4000,
            });
            queryClient.invalidateQueries(['invoice-builder']);
        } catch (commandError: any) {
            showAppNotification({
                title: 'Could not update invoice',
                description: commandError?.message || 'Please try again.',
                icon: 'error',
                timeout: 5000,
            });
        }
        hideAppSpinner();
    }, [patchInvoice, queryClient]);

    const actionsValue = useMemo(
        () => ({
            organizationId,
            onView,
            onEdit,
            onPrint,
            onDownload,
            onMarkSent: (invoice: InvoiceRow) => runCommand(invoice, { type: 'markSent' }),
            onMarkPaid: (invoice: InvoiceRow) => runCommand(invoice, { type: 'markPaid' }),
            onVoid: setVoidTarget,
        }),
        [organizationId, onView, onEdit, onPrint, onDownload, runCommand],
    );

    if (unavailable) {
        return (
            <EmptyState>
                <EmptyTitle>Invoice Builder is not enabled</EmptyTitle>
                <EmptyDescription>
                    Invoice Builder is not available for this organization.
                    A platform admin must enable the app before invoices can be created.
                </EmptyDescription>
            </EmptyState>
        );
    }

    if (error) {
        return (
            <EmptyState>
                <EmptyTitle>Could not load invoices</EmptyTitle>
                <EmptyDescription>
                    {error.message || 'Something went wrong. Please try again.'}
                </EmptyDescription>
            </EmptyState>
        );
    }

    return (
        <InvoiceActionsContext.Provider value={actionsValue}>
            <TableContainer>
                <Table
                    keyField="id"
                    data={rows}
                    isLoading={isLoading || isFetching}
                    emptyTitle="No invoices yet"
                    emptyDescription="Create an invoice to get started."
                >
                    <Column
                        header="Invoice"
                        field="number"
                        component={InvoiceColumn}
                        onClick={onView}
                    />
                    {!hideCustomerColumn && (
                        <Column header="Customer" field="customer" component={CustomerColumn} />
                    )}
                    <Column header="Status" field="status" component={StatusColumn} defaultWidth={140} />
                    <Column header="Issued" field="issuedOn" component={IssuedColumn} defaultWidth={200} />
                    <Column header="Due" field="dueOn" component={DueColumn} defaultWidth={200} />
                    <Column header="Amount" field="total" component={AmountColumn} defaultWidth={250} />
                    <Column
                        header="Actions"
                        field="id"
                        defaultWidth={120}
                        component={TableActions}
                    />
                </Table>
                <PaginationContainer>
                    <EntriesText>
                        {`Showing ${invoices.length} invoice${invoices.length === 1 ? '' : 's'}`}
                    </EntriesText>
                    <PaginationButtons>
                        <ButtonIcon
                            title="Previous"
                            icon={<ChevronLeft />}
                            borderRadius="semi-square"
                            onClick={goPrev}
                            size="small"
                            disabled={isLoading || pageIndex === 0}
                            variant="border-filled"
                        />
                        <ButtonIcon
                            title="Next"
                            icon={<ChevronRight />}
                            size="small"
                            borderRadius="semi-square"
                            onClick={goNext}
                            disabled={isLoading || !nextCursor}
                            variant="border-filled"
                        />
                    </PaginationButtons>
                </PaginationContainer>
            </TableContainer>
            <VoidInvoiceModal
                isOpen={Boolean(voidTarget)}
                invoiceNumber={voidTarget?.number}
                isSubmitting={isPatching}
                onRequestClose={() => setVoidTarget(undefined)}
                onConfirm={(note) => {
                    if (!voidTarget) return;
                    const invoice = voidTarget;
                    setVoidTarget(undefined);
                    runCommand(invoice, { type: 'void', note });
                }}
            />
        </InvoiceActionsContext.Provider>
    );
};

export default InvoiceList;
